/**
 * P2a (RCA 2026-08-03) — deterministic interconnection-queue duration check.
 *
 * The Redhead feasibility narrative claimed interconnection "queue delays
 * averaging over 5 years" while the SED's `grid.data.estimatedQueueTime` said
 * "24-48 months" — and no deterministic detector could see it: the grounding
 * validator had no years pattern (and is off in prod), the Phase-4 legacy
 * fact-check has no queue rule, and the canonical-fact reconciler carries no
 * timeline fact. Only the LLM reviewer noticed, unreliably.
 *
 * This module closes that gap with a sentence-scoped comparison that runs in
 * the shared quality pipeline on every path:
 *   - the SED estimate parses to a `[lo, hi]` range in years,
 *   - every narrative sentence naming the queue/interconnection AND a
 *     duration is compared against that range,
 *   - a claim beyond the range (± tolerance) yields ONE sanity finding.
 *
 * Non-blocking by design (mirrors the Phase-4 fact-check contract): the
 * finding surfaces to the reviewer context, the report's sanity list, and the
 * decision-analysis quality findings — it never gates persistence by itself.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SanityFinding } from '../../types/siteRag';
import { validateSemanticFactBinding } from './semanticFactBinding';

const DURATION_RE =
  /(\d+(?:\.\d+)?)\s*(?:(?:[–—-]|to)\s*(\d+(?:\.\d+)?))?\s*(months?|mos?\b|years?|yrs?\b)/i;
const QUEUE_CONTEXT_RE = /\b(queue|interconnection|energi[sz]ation)\b/i;
/** Slack around the SED range before a claim counts as contradicting (years). */
const TOLERANCE_YEARS = 0.5;

export interface QueueDurationRange {
  loYears: number;
  hiYears: number;
  source: string;
}

/** Parse "24-48 months" / "3-5 years" / "4 years" to a year range. */
export function parseQueueDurationRange(raw: unknown): QueueDurationRange | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const m = raw.match(DURATION_RE);
  if (!m) return null;
  const lo = Number.parseFloat(m[1]!);
  const hi = m[2] ? Number.parseFloat(m[2]) : lo;
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
  const divisor = /^mo/i.test(m[3]!) ? 12 : 1;
  const loYears = Math.min(lo, hi) / divisor;
  const hiYears = Math.max(lo, hi) / divisor;
  if (loYears < 0.05 || hiYears > 40) return null;
  return { loYears, hiYears, source: raw.trim() };
}

/** Pull every string leaf out of the narrative object (bounded).
 *  Shared with the sibling deterministic claim checks (elevation). */
export function collectNarrativeStrings(value: unknown): string[] {
  const out: string[] = [];
  collectStrings(value, out);
  return out;
}

function collectStrings(value: unknown, out: string[], depth = 0): void {
  if (out.length > 4000 || depth > 6 || value == null) return;
  if (typeof value === 'string') {
    if (value.length >= 20) out.push(value);
    return;
  }
  if (Array.isArray(value)) {
    for (const entry of value) collectStrings(entry, out, depth + 1);
    return;
  }
  if (typeof value === 'object') {
    for (const entry of Object.values(value as Record<string, unknown>)) {
      collectStrings(entry, out, depth + 1);
    }
  }
}

/**
 * True when a duration mention in `sentence` is explicitly scoped as a
 * regional/national generation or storage queue benchmark (e.g. Berkeley Lab
 * *Queued Up* median request-to-commercial-operation duration for generation
 * and storage projects) rather than this site's load-interconnection queue.
 *
 * Requires explicit generation/storage cohort or benchmark attribution (or an
 * explicit disclaimer that the figure is not a site/load timetable) AND must
 * not assert that this site's confirmed queue duration is that figure.
 */
function isScopedGeneratorQueueBenchmark(sentence: string, localWindow: string): boolean {
  const confirmsSiteQueue =
    /\b(?:confirmed|actual|assigned|utility[- ]confirmed)\s+(?:site\s+)?(?:interconnection\s+)?queue\s+(?:duration|time|schedule|timeline)\b/i.test(
      localWindow,
    ) ||
    /\bthis\s+(?:site|project|campus)(?:['’]s)?\s+(?:confirmed\s+)?(?:interconnection\s+)?queue\s+(?:duration|time|will\s+take|is\s+confirmed\s+at)\b/i.test(
      localWindow,
    );
  if (confirmsSiteQueue) return false;

  const hasLbnlOrQueuedUp = /\b(?:berkeley\s+lab|lbnl|queued\s+up)\b/i.test(sentence);
  const hasGenStorageCohort =
    /\b(?:generation(?:\s+and\s+storage|\s*\/\s*storage)?|generators?(?:\s+and\s+storage)?|storage\s+projects?)\b/i.test(
      sentence,
    ) &&
    /\b(?:median|benchmark|cohort|built\s+in\s+20\d{2}|commercial\s+operation|regional|national)\b/i.test(
      sentence,
    );
  const hasExplicitNonSiteDisclaimer =
    /\bnot\s+(?:a|an|the|this\s+site['’]?s?)\s+(?:direct\s+analog\s+for\s+load|(?:data[- ]cent(?:er|re)\s+|large[- ]load\s+|site[- ]specific\s+|site\s+|project\s+)?(?:load[- ](?:connection|service)\s+|interconnection\s+)?(?:timetable|queue(?:\s+duration|\s+estimate|\s+timeline)?|schedule))\b/i.test(
      sentence,
    );

  return hasLbnlOrQueuedUp || hasGenStorageCohort || hasExplicitNonSiteDisclaimer;
}

/**
 * True when a duration mention in `sentence` describes a customer-requested
 * load-ramp cadence / staged milestone interval (e.g. ~6-month steps between
 * 100 MW Q4 2027, 350 MW Q2 2028, … in Sheet1 B36) rather than an
 * interconnection queue duration.
 */
function isCustomerLoadRampCadence(sentence: string, localWindow: string): boolean {
  const confirmsSiteQueue =
    /\b(?:interconnection\s+queue|queue\s+duration|queue\s+delay|queue\s+time)\s+(?:is|of|takes?|averaging|requires?)\b/i.test(
      localWindow,
    ) &&
    !/\bnot\s+(?:an?\s+)?(?:interconnection\s+)?queue\b/i.test(localWindow);
  if (confirmsSiteQueue) return false;

  const hasLoadRampCadence =
    /\b(?:load[- ]ramp|ramp[- ]up|staged\s+load|phased\s+load|milestone|phase|step|increment)\b/i.test(
      sentence,
    ) &&
    /\b(?:cadence|intervals?|steps?|increments?|spacing|pacing|between\s+(?:phases|milestones|tranches|steps)|every\s+\d+\s*months?|\d+(?:\.\d+)?[- ]month\s+(?:cadence|intervals?|steps?|increments?|ramp|staging))\b/i.test(
      sentence,
    );

  const hasExplicitNotQueueDisclaimer =
    /\b(?:not\s+(?:an?\s+|the\s+)?(?:interconnection\s+)?queue(?:\s+duration|\s+time|\s+estimate|\s+schedule)?|rather\s+than\s+(?:an?\s+)?(?:interconnection\s+)?queue)\b/i.test(
      localWindow,
    ) ||
    /\b(?:customer[- ]requested|questionnaire|sheet\s*1|cell\s+b36)\b/i.test(sentence) &&
      /\b(?:cadence|intervals?|ramp|milestones?)\b/i.test(localWindow);

  return hasLoadRampCadence || hasExplicitNotQueueDisclaimer;
}

/**
 * Compare narrative queue-duration claims against the SED estimate.
 * Emits at most 3 findings (deduped by claimed value) to avoid a storm when
 * one figure repeats across sections.
 */
export function checkQueueDurationClaims(
  geminiData: unknown,
  sed: Pick<SiteEvidenceDocument, 'grid'> | null | undefined,
): SanityFinding[] {
  const estimate = parseQueueDurationRange(
    (sed as { grid?: { data?: { estimatedQueueTime?: unknown } } } | null | undefined)
      ?.grid?.data?.estimatedQueueTime,
  );
  if (!estimate) return [];

  const texts: string[] = [];
  collectStrings(geminiData, texts);
  const findings: SanityFinding[] = [];
  const seenValues = new Set<string>();
  const globalDurationRe = new RegExp(DURATION_RE.source, 'gi');

  for (const text of texts) {
    for (const sentence of text.split(/(?<=[.!?])\s+|\n+/)) {
      if (!QUEUE_CONTEXT_RE.test(sentence)) continue;
      for (const m of sentence.matchAll(globalDurationRe)) {
        const matchIndex = m.index ?? 0;
        const localWindow = sentence.slice(
          Math.max(0, matchIndex - 120),
          Math.min(sentence.length, matchIndex + m[0].length + 120),
        );
        if (isScopedGeneratorQueueBenchmark(sentence, localWindow)) continue;
        if (isCustomerLoadRampCadence(sentence, localWindow)) continue;
        if (
          !validateSemanticFactBinding({
            factId: 'grid.queueYears',
            claimText: sentence,
            observedText: m[0],
          }).ok
        ) {
          continue;
        }

        const claim = parseQueueDurationRange(m[0]);
        if (!claim) continue;
        const beyondHigh = claim.loYears > estimate.hiYears + TOLERANCE_YEARS;
        const beyondLow = claim.hiYears < estimate.loYears - TOLERANCE_YEARS;
        if (!beyondHigh && !beyondLow) continue;
        const key = `${claim.loYears}-${claim.hiYears}`;
        if (seenValues.has(key)) continue;
        seenValues.add(key);
        findings.push({
          id: `queue-duration-${key}`,
          // 'inconsistency' WITHOUT verificationStatus: display-only surfacing,
          // never selected by the Phase-5c confirmed-inconsistency repair.
          category: 'inconsistency',
          section: 'gridStudy',
          finding:
            `Interconnection queue duration claim "${m[0].trim()}" is outside the SED estimate `
            + `"${estimate.source}" (${estimate.loYears.toFixed(1)}–${estimate.hiYears.toFixed(1)} years).`,
          suggestion:
            'Align the narrative with the SED queue estimate, or cite the document that supports the longer figure.',
          severity: 'high',
        });
        if (findings.length >= 3) return findings;
      }
    }
  }
  return findings;
}

/**
 * True when an existing stored queue-duration sanity finding or review issue
 * ("Interconnection queue duration claim \"X\" is outside the SED estimate...")
 * refers exclusively to contextual construction/heuristic duration clauses in
 * narrativeTexts that validateSemanticFactBinding('grid.queueYears') rejects.
 */
export function isFalsePositiveQueueDurationFindingText(
  findingText: string,
  narrativeTexts: readonly string[],
): boolean {
  const m =
    /Interconnection queue duration claim\s+["“]([^"”]+)["”]\s+is outside the SED estimate/i.exec(
      findingText,
    );
  if (!m) return false;
  const claimedDuration = m[1]!.trim();
  const durationNorm = claimedDuration.toLowerCase();
  let matchedAnySentence = false;
  for (const text of narrativeTexts) {
    for (const sentence of text.split(/(?<=[.!?])\s+|\n+/)) {
      if (!sentence.toLowerCase().includes(durationNorm)) continue;
      if (!QUEUE_CONTEXT_RE.test(sentence)) continue;
      matchedAnySentence = true;
      const check = validateSemanticFactBinding({
        factId: 'grid.queueYears',
        claimText: sentence,
        observedText: claimedDuration,
      });
      if (check.ok) return false;
    }
  }
  return matchedAnySentence;
}
